/* eslint-disable max-len */
/* eslint-disable semi */
import { Box, Divider, Typography, FormHelperText } from "@mui/material";
import { useSnackbar } from "notistack";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import BackButton from "../../components/Buttons/BackButton";
import CreateProfessionalSteps from "../../components/FormContainers/CreateProfessional/CreateProfessionalSteps";
import { IProfForm } from "../../components/FormContainers/CreateProfessional/type";
import { MapPoint } from "../../components/Maps/types";
import FormSkeleton from "../../components/Skeletons/FormSkeleton";
import {
  AppleTitleNamingKeys,
  Ilevels,
  Title_Naming,
} from "../../lib/generalTypes";
import { IntExt, Permissions } from "../../lib/permissions";
import {
  createApplication,
  getApplicationDetails,
  getApplicationDetailsOmeka,
  getApplicationFiles,
  getProfessionalsDropdownOptions,
  updateProfessional,
} from "../../lib/static/professionals/fetchers";
import { createProfessionalInitialValues } from "../../lib/static/professionals/initialValues";
import UserService from "../../services/UserService";
import Theme from "../../Themes/main.theme";
import PreviewProfessional from "./PreviewProfessional";
import AsyncBackdropSubmit from "../../components/AsyncBackdropSubmit";
import { useUserDbInfo } from "../../hooks/UserInfo";
import { useFromAccessLogic } from "../../hooks/useFromAccessLogic";

const EditProfessional = () => {
  const [data, setData] = React.useState<any>();
  const [renderForm, setRenderForm] = React.useState<boolean>(true);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [isLoadingSubmit, setIsLoadingSubmit] = React.useState<boolean>(false);
  const [isLoadingParametrics, setIsLoadingParametrics] =
    React.useState<boolean>(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [level1Options, setlevel1Options] = React.useState<Ilevels | []>([]);
  const [languages, setLanguages] = React.useState<Ilevels | []>([]);
  const [regions, setRegions] = React.useState<Ilevels | []>([]);
  const [regionsOnly, setRegionsOnly] = React.useState<Ilevels | []>([]);
  const [specialtiesLevel2, setSpecialtiesLevel2] = React.useState<
    Ilevels | []
  >([]);
  const renderFromOmeka = searchParams.get("omeka");
  const is_external = UserService.hasRole([IntExt.External]);

  const { userDbInfo } = useUserDbInfo();

  const { availableForms, isLoading: isFormPublishedLoading } =
    useFromAccessLogic(AppleTitleNamingKeys.EditProfessional);

  const { id } = useParams();

  const navigate = useNavigate();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const convertJsonBodyToFormData = (body: IProfForm) => {
    const _media: string[] = [];
    const _attachments: string[] = [];

    const formData = new FormData();
    if (body?.thumbnail && typeof body?.thumbnail !== "string") {
      if (body?.thumbnail.length === 0) {
        delete body.thumbnail;
      } else {
        formData.append(
          "file",
          new Blob(body?.thumbnail, {
            type: body?.thumbnail[0].type,
          }),
          encodeURI(body?.thumbnail[0].name)
        );
      }
      delete body.thumbnail;
    }
    if (body?.resume && typeof body?.resume !== "string") {
      if (body?.resume.length === 0) {
        delete body.resume;
      } else {
        formData.append(
          "resume",
          new Blob(body?.resume, {
            type: body?.resume[0].type,
          }),
          encodeURI(body?.resume[0].name)
        );
      }
      delete body.resume;
    }
    if (Array.isArray(body?.media)) {
      body?.media.map((x: File) => {
        if (typeof x === "string") {
          _media.push(x);
        } else
          formData.append(
            "media",
            new Blob([x], {
              type: x.type,
            }),
            encodeURI(x.name)
          );
      });
      delete body.media;
    }
    if (Array.isArray(body?.attached_files)) {
      body?.attached_files.map((x: File) => {
        if (typeof x === "string") {
          _attachments.push(x);
        } else {
          formData.append(
            "attachments",
            new Blob([x], {
              type: x.type,
            }),
            encodeURI(x.name)
          );
        }
      });
      delete body.attached_files;
    }
    formData.append(
      "form",
      JSON.stringify({
        ...body,
        ...(_media.length > 0 && { media: _media }),
        ...(_attachments.length > 0 && { attachments: _attachments }),
      })
    );
    return formData;
  };

  const onSubmit = async (values: IProfForm, geolocation: MapPoint) => {
    setIsLoadingSubmit(true);

    const addedValues = {
      ...values,
      specialty: [
        ...new Set([
          ...(values?.specialty_lvl2?.map((x) => x.parent_object) || []),
        ]),
      ],
      latitude: geolocation?.lat
        ? String(geolocation?.lat)
        : null,
      longitude: geolocation?.lng
        ? String(geolocation?.lng)
        : null,
    };
    const formData = convertJsonBodyToFormData(addedValues);

    try {
      const resp = renderFromOmeka
        ? await createApplication(formData, id)
        : await updateProfessional(data.id, formData);
      const files = await getApplicationFiles(resp.data.application_id);
      searchParams.set("preview", "true");
      searchParams.delete("omeka");
      if (resp?.status === 201) {
        sessionStorage.setItem(
          "application_id",
          resp?.data?.application_id.toString()
        );
      }
      setSearchParams(searchParams);

      setRenderForm(false);

      setData({
        ...resp.data,
        city: resp.data.city,
        media: files.data.media,
        thumbnail: files.data.thumbnail,
        resume: files.data.resume,
        attached_files: files.data.attached_files,
      });

      enqueueSnackbar(
        t(
          "Το αίτημα καταχώρισης επαγγελματία / επιχείρησης επεξεργάστηκε επιτυχώς"
        ),
        { variant: "success" }
      );
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        t(
          "Η επεξργασία του αιτήματος καταχώρισης επαγγελματία / επιχείρησης απέτυχε, παρακαλώ προσπαθήστε ξανά."
        ),
        { variant: "error" }
      );
    }
    setIsLoadingSubmit(false);
  };

  const getParametrics = async () => {
    setIsLoadingParametrics(true);

    const { services, languages, regions, regions_only, specialty_level_2 } =
      await getProfessionalsDropdownOptions();

    setlevel1Options(
      services.map((service: any) => {
        return {
          key: t(service.key),
          value: service.value,
        };
      })
    );
    setLanguages(
      languages.map((language: any) => {
        return {
          key: t(language.key),
          value: language.value,
        };
      })
    );
    setRegions(
      regions.map((region: any) => {
        return {
          key: t(region.key),
          value: region.value,
        };
      })
    );
    setRegionsOnly(
      regions_only.map((_region: any) => {
        return {
          key: t(_region.key),
          value: _region.value,
        };
      })
    );
    setSpecialtiesLevel2(
      specialty_level_2.map((level2: any) => {
        return {
          key: t(level2.key),
          value: level2.value,
        };
      })
    );

    setIsLoadingParametrics(false);
  };

  const getData = async (id: string) => {
    try {
      let detailsProfessional;
      if (renderFromOmeka) {
        detailsProfessional = await getApplicationDetailsOmeka(id);

        if (
          !detailsProfessional.is_applicant &&
          !UserService.hasRole([
            Permissions.PermissionInternalProfessionalsApplicationModificationRequestCreateMy_org,
            Permissions.PermissionInternalProfessionalsApplicationModificationRequestCreateAll,
            Permissions.PermissionExternalProfessionalApplicationModificationCreateMy,
            Permissions.PermissionExternalProfessionalApplicationCreateMy,
          ])
        ) {
          if (!is_external) {
            navigate("/admin/professionals/dashboard");
          } else {
            navigate("/professionals");
          }
          enqueueSnackbar(
            t("Δεν έχετε δικαίωμα επεξεργασίας του Επαγγελματία"),
            { variant: "error" }
          );
        }
      } else if (sessionStorage.getItem("application_id")) {
        const appl_id = sessionStorage.getItem("application_id");
        detailsProfessional = appl_id && (await getApplicationDetails(appl_id));
      } else {
        detailsProfessional = await getApplicationDetails(id);
      }
      setData({ ...detailsProfessional, city: detailsProfessional.city.value });

      if (is_external) return setIsLoading(false);

      if (
        UserService.hasRole([
          Permissions.PermissionInternalProfessionalsApplicationCreateAll,
        ])
      ) {
        setIsLoading(false);
      } else if (
        UserService.hasRole([
          Permissions.PermissionInternalProfessionalsApplicationCreateMy_org,
        ]) &&
        !UserService.hasRole([
          Permissions.PermissionInternalProfessionalsApplicationCreateAll,
        ])
      ) {
        userDbInfo.administrative_unit_id ==
          detailsProfessional
            .loc_regional_unit_municipalities_loc_regional_unit_municipalitiesToprof_professional_city
            .usr_administrative_unit.id;
        setIsLoading(false);
      } else {
        enqueueSnackbar(t("Δεν έχετε δικαίωμα επεξεργασίας του Επαγγελματία"), {
          variant: "error",
        });
        navigate(-1);
      }
    } catch (error: any) {
      if (error.response.status === 401) {
        enqueueSnackbar(t("Δεν έχετε δικαίωμα επεξεργασίας του επαγγελματία"), {
          variant: "error",
        });
        navigate(-1);
      }
      console.error(error);
    }
  };

  React.useEffect(() => {
    if (is_external && !isFormPublishedLoading) {
      if (!availableForms.is_published) {
        enqueueSnackbar(t("Το αίτημα δεν είναι διαθέσιμο αυτή τη στιγμή."), {
          variant: "info",
        });
        navigate("/professionals");
      }
    }
  }, [isFormPublishedLoading]);

  React.useEffect(() => {
    getParametrics();
    if (id) {
      getData(id);
    }
    Object.prototype.hasOwnProperty.call(
      Object.fromEntries([...searchParams]),
      "preview"
    ) && setRenderForm(false);
  }, []);

  document.title =
    data?.type_of_application !== Title_Naming.UpdateProfessional
      ? is_external
        ? t("Αιτήμα Τροποποίησης Στοιχείων Επαγγελματία / Επιχείρησης")
        : t(
          "Εσωτερικό Αιτήμα Τροποποίησης Στοιχείων Επαγγελματία / Επιχείρησης"
        )
      : is_external
        ? t("Επεξεργασία Αιτήματος Καταχώρισης Επαγγελματία / Επιχείρησης")
        : t(
          "Επεξεργασία Εσωτερικού Αιτήματος Καταχώρισης Επαγγελματία / Επιχείρησης"
        );

  return (
    <>
      <AsyncBackdropSubmit isLoading={isLoadingSubmit}>
        {t("Παρακαλώ περιμένετε μέχρι να ολοκληρωθεί η διαδικασία...")}
      </AsyncBackdropSubmit>
      {renderForm
        ? (
          <>
            <Box mt={2} mb={2}>
              <BackButton onClick={() => navigate(-1)} />
            </Box>
            {isLoading || isLoadingParametrics
              ? (
                <Box mt={4}>
                  <FormSkeleton lines={6} />
                </Box>
              )
              : (
                <>
                  {is_external && !data?.is_applicant && renderFromOmeka
                    ? (
                      <Box display='flex' justifyContent='center'>
                        <Typography variant='h3'>
                          {t("Δεν μπορείτε να επεξεργαστείτε το Αίτημα")}
                        </Typography>
                      </Box>
                    )
                    : (
                      <>
                        <Typography
                          sx={{ my: 2 }}
                          variant='h2'
                          color={Theme?.labelColor?.main}
                        >
                          {data?.type_of_application !==
                      Title_Naming.UpdateProfessional
                            ? is_external
                              ? t(
                                "Αιτήμα Τροποποίησης Στοιχείων Επαγγελματία / Επιχείρησης"
                              )
                              : t(
                                "Εσωτερικό Αιτήμα Τροποποίησης Στοιχείων Επαγγελματία / Επιχείρησης"
                              )
                            : is_external
                              ? t(
                                "Επεξεργασία Αιτήματος Καταχώρισης Επαγγελματία / Επιχείρησης"
                              )
                              : t(
                                "Επεξεργασία Εσωτερικού Αιτήματος Καταχώρισης Επαγγελματία / Επιχείρησης"
                              )}
                        </Typography>
                        <Divider
                          sx={{ mb: 5, border: 1, color: Theme?.dividerColor?.main }}
                        />
                        {data?.status === "draft"
                          ? (
                            <FormHelperText>
                              <Typography
                                variant='body1'
                                fontStyle='italic'
                                sx={{
                                  marginTop: "16px",
                                  fontWeight: "bolder",
                                }}
                              >
                                {t(
                                  "Πρέπει να συμπληρωθούν όλα τα υποχρεωτικά πεδία, ώστε να κάνετε οριστική υποβολή του αιτήματός σας."
                                )}
                              </Typography>
                            </FormHelperText>
                          )
                          : null}
                        <CreateProfessionalSteps
                          edit
                          initialValues={{
                            ...createProfessionalInitialValues,
                            ...data,
                          }}
                          level1Options={level1Options}
                          languages={languages}
                          regions={regions}
                          regionsOnly={regionsOnly}
                          specialtiesLevel2={specialtiesLevel2}
                          onSubmit={onSubmit}
                        />
                      </>
                    )}
                </>
              )}
          </>
        )
        : (
          <PreviewProfessional
            isLoading={isLoading || isFormPublishedLoading}
            data={data}
            setRenderForm={setRenderForm}
          />
        )}
    </>
  );
};

export default EditProfessional;
